"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isSupplementalDataElementStatement = exports.isStatementFunction = exports.findLocalIdForLibraryRef = exports.findAllLocalIdsInSort = exports.findAllLocalIdsInStatement = exports.findAllLocalIdsInStatementByName = void 0;
/**
 * Finds all localIds in a statement by it's library and statement name.
 * @public
 * @param {ELM} libraryElm - The library the statement belongs to.
 * @param {string} statementName - The statement name to search for.
 * @return {Hash} List of local ids in the statement.
 */
function findAllLocalIdsInStatementByName(libraryElm, statementName) {
    // create place for aliases and their usages to be placed to be filled in later. Aliases and their usages (aka scope)
    // and returns do not have localIds in the elm but do in elm_annotations at a consistent calculable offset.
    // BE WARY of this calaculable offset.
    var emptyResultClauses = [];
    var statement = libraryElm.library.statements.def.find(function (stat) { return stat.name === statementName; });
    var libraryName = libraryElm.library.identifier.id;
    var aliasMap = {};
    // recurse through the statement elm for find all localIds
    var localIds = findAllLocalIdsInStatement(statement, libraryName, statement === null || statement === void 0 ? void 0 : statement.annotation, {}, aliasMap, emptyResultClauses, null);
    // Create/change the clause for all aliases and their usages
    for (var _i = 0, _a = Array.from(emptyResultClauses); _i < _a.length; _i++) {
        var alias = _a[_i];
        // Only do it if we have a clause for where the result should be fetched from
        // and have a localId for the clause that the result should map to
        if (localIds[alias.expressionLocalId] != null && alias.aliasLocalId != null) {
            localIds[alias.aliasLocalId] = {
                localId: alias.aliasLocalId,
                sourceLocalId: alias.expressionLocalId
            };
        }
    }
    // We do not yet support coverage/coloring of Function statements
    // Mark all the clauses as unsupported so we can mark them 'NA' in the clause_results
    if ((statement === null || statement === void 0 ? void 0 : statement.type) === 'FunctionDef') {
        for (var localId in localIds) {
            var clause = localIds[localId];
            clause.isUnsupported = true;
        }
    }
    return localIds;
}
exports.findAllLocalIdsInStatementByName = findAllLocalIdsInStatementByName;
/**
 * Finds all localIds in the statement structure recursively.
 * @private
 * @param {ELMStatement | any} statement - The statement structure or child parts of it.
 * @param {String} libraryName - The name of the library we are looking at.
 * @param {any[]} annotation - The JSON annotation for the entire structure, this is occasionally needed.
 * @param {any} localIds - The hash of localIds we are filling.
 * @param {any} aliasMap - The map of aliases.
 * @param {Array} emptyResultClauses - List of clauses that will have empty results from the engine. Each object on
 *    this has info on where to find the actual result.
 * @param {any} parentNode - The parent node, used for some special situations.
 * @return {any} List of local ids in the statement. This is same array, localIds, that is passed in.
 */
function findAllLocalIdsInStatement(statement, libraryName, annotation, localIds, aliasMap, emptyResultClauses, parentNode) {
    // looking at the key and value of everything on this object or array
    for (var k in statement) {
        var alId = void 0;
        var v = statement[k];
        if (k === 'return') {
            // Keep track of the localId of the expression that the return references. 'from's without a 'return' dont have
            // localId's. So it doesn't make sense to mark them.
            if (statement.return.expression.localId != null) {
                aliasMap[v] = statement.return.expression.localId;
                alId = statement.return.localId;
                if (alId) {
                    emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: aliasMap[v] });
                }
            }
            findAllLocalIdsInStatement(v, libraryName, annotation, localIds, aliasMap, emptyResultClauses, statement);
        }
        else if (k === 'alias') {
            if (statement.expression != null && statement.expression.localId != null) {
                // Keep track of the localId of the expression that the alias references
                aliasMap[v] = statement.expression.localId;
                // Determine the localId in the elm_annotation for this alias.
                alId = parseInt(statement.expression.localId, 10) + 1;
                emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: aliasMap[v] });
            }
        }
        else if (k === 'scope') {
            // The scope entry references an alias but does not have an ELM local ID. Hoever it DOES have an elm_annotations localId
            // The elm_annotation localId of the alias variable is the localId of it's parent (one less than)
            // because the result of the scope clause should be equal to the clause that the scope is referencing
            alId = parseInt(statement.localId, 10) - 1;
            emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: aliasMap[v] });
        }
        else if (k === 'asTypeSpecifier') {
            // Map the localId of the asTypeSpecifier (Code, Quantity...) to the result of the result it is referencing
            // For example, in the CQL code 'Variable.result as Code' the typeSpecifier does not produce a result, therefore
            // we will set its result to whatever the result value is for 'Variable.result'
            alId = statement.asTypeSpecifier.localId;
            if (alId != null) {
                var typeClauseId = parseInt(statement.asTypeSpecifier.localId, 10) - 1;
                emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: typeClauseId });
            }
        }
        else if (k === 'sort') {
            // Sort is a special case that we need to recurse into separately and set the results to the result of the statement the sort clause is in
            findAllLocalIdsInSort(v, libraryName, localIds, aliasMap, emptyResultClauses, parentNode);
        }
        else if (k === 'let') {
            // let is a special case where it is an array, one for each defined alias. These aliases work slightly different
            // and execution engine does return results for them on use. The Initial naming of them needs to be properly pointed
            // to what they are set to.
            var aLet = void 0;
            for (var _i = 0, _a = Array.from(v); _i < _a.length; _i++) {
                aLet = _a[_i];
                // Add the localId for the definition of this let to it's source.
                localIds[aLet.localId] = { localId: aLet.localId, sourceLocalId: aLet.expression.localId };
                findAllLocalIdsInStatement(aLet.expression, libraryName, annotation, localIds, aliasMap, emptyResultClauses, statement);
            }
            // If 'First' and 'Last' expressions, the result of source of the clause should be set to the expression
        }
        else if (k === 'type' && (v === 'First' || v === 'Last')) {
            if (statement.source && statement.source.localId != null) {
                alId = statement.source.localId;
                emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: statement.localId });
            }
            // Continue to recurse into the 'First' or 'Last' expression
            findAllLocalIdsInStatement(v, libraryName, annotation, localIds, aliasMap, emptyResultClauses, statement);
            // If this is a FunctionRef or ExpressionRef and it references a library, find the clause for the library reference and add it.
        }
        else if (k === 'type' && (v === 'FunctionRef' || v === 'ExpressionRef') && statement.libraryName != null) {
            var libraryClauseLocalId = findLocalIdForLibraryRef(annotation, statement.localId, statement.libraryName);
            if (libraryClauseLocalId !== null) {
                // only add the clause if the localId for it is found
                // the sourceLocalId is the FunctionRef itself to match how library statement references work.
                localIds[libraryClauseLocalId] = { localId: libraryClauseLocalId, sourceLocalId: statement.localId };
            }
            // else if they key is localId push the value
        }
        else if (k === 'localId') {
            localIds[v] = { localId: v };
            // if the value is an array or object, recurse
        }
        else if (Array.isArray(v) || typeof v === 'object') {
            findAllLocalIdsInStatement(v, libraryName, annotation, localIds, aliasMap, emptyResultClauses, statement);
        }
    }
    return localIds;
}
exports.findAllLocalIdsInStatement = findAllLocalIdsInStatement;
/**
 * Finds all localIds in the sort structure recursively and sets the expressionLocalId to the parent statement.
 * @private
 * @param {any} statement - The statement structure or child parts of it.
 * @param {String} libraryName - The name of the library we are looking at.
 * @param {any} localIds - The hash of localIds we are filling.
 * @param {any} aliasMap - The map of aliases.
 * @param {any[]} emptyResultClauses - List of clauses that will have empty results from the engine. Each object on
 *    this has info on where to find the actual result.
 * @param {any} rootStatement - The rootStatement.
 */
function findAllLocalIdsInSort(statement, libraryName, localIds, aliasMap, emptyResultClauses, rootStatement) {
    var alId = statement.localId;
    emptyResultClauses.push({ lib: libraryName, aliasLocalId: alId, expressionLocalId: rootStatement.localId });
    return (function () {
        var result = [];
        for (var k in statement) {
            var v = statement[k];
            if (Array.isArray(v) || typeof v === 'object') {
                result.push(findAllLocalIdsInSort(v, libraryName, localIds, aliasMap, emptyResultClauses, rootStatement));
            }
            else {
                result.push(undefined);
            }
        }
        return result;
    })();
}
exports.findAllLocalIdsInSort = findAllLocalIdsInSort;
/**
 * Find the localId of the library reference in the JSON elm annotation. This recursively searches the annotation structure
 * for the clause of the library ref. When that is found it knows where to look inside of that for where the library
 * reference may be.
 *
 * Consider the following example of looking for function ref with id "55" and library "global".
 * CQL for this is "global.CalendarAgeInYearsAt(...)". The following annotation snippet covers the call of the
 * function.
 *
 * {
 *  "r": "55",
 *  "s": [
 *    {
 *      "r": "49",
 *      "s": [
 *        {
 *          "value": [
 *            "global"
 *          ]
 *        }
 *      ]
 *    },
 *    {
 *      "value": [
 *        "."
 *      ]
 *    },
 *    {
 *      "r": "55",
 *      "s": [
 *        {
 *          "value": [
 *            "\"CalendarAgeInYearsAt\"",
 *            "("
 *          ]
 *        },
 *
 * This method will recurse through the structure until it stops on this snippet that has "r": "55". Then it will check
 * if the value of the first child is simply an array with a single string equaling "global". If that is indeed the
 * case then it will return the "r" value of that first child, which is the clause localId for the library part of the
 * function reference. If that is not the case, it will keep recursing and may eventually return null.
 *
 * @private
 * @param {any|any[]} annotation - The annotation structure or child in the annotation structure.
 * @param {string} refLocalId - The localId of the library ref we should look for.
 * @param {string} libraryName - The library reference name, used to find the clause.
 * @return {string|null} The localId of the clause for the library reference or null if not found.
 */
function findLocalIdForLibraryRef(annotation, refLocalId, libraryName) {
    // if this is an object it should have an "r" for localId and "s" for children or leaf nodes
    var child;
    var ret;
    if (Array.isArray(annotation)) {
        for (var _i = 0, _a = Array.from(annotation); _i < _a.length; _i++) {
            child = _a[_i];
            // in the case of a list of children only return if there is a non null result
            ret = findLocalIdForLibraryRef(child, refLocalId, libraryName);
            if (ret !== null) {
                return ret;
            }
        }
    }
    else if (typeof annotation === 'object') {
        // if we found the function ref
        if (annotation.r != null && annotation.r === refLocalId) {
            // check if the first child has the first leaf node with the library name
            // refer to the method comment for why this is done.
            if (__guard__(annotation.s[0].s != null ? annotation.s[0].s[0].value : undefined, function (x) { return x[0]; }) === libraryName) {
                // return the localId if there is one
                if (annotation.s[0].r != null) {
                    return annotation.s[0].r;
                }
                // otherwise return null because the library ref is in the same clause as extpression ref.
                // this is common with expressionRefs for some reason.
                return null;
            }
        }
        // if we made it here, we should travserse down the child nodes
        if (Array.isArray(annotation.s)) {
            for (var _b = 0, _c = Array.from(annotation.s); _b < _c.length; _b++) {
                child = _c[_b];
                // in the case of a list of children only return if there is a non null result
                ret = findLocalIdForLibraryRef(child, refLocalId, libraryName);
                if (ret !== null) {
                    return ret;
                }
            }
        }
        else if (typeof annotation.s === 'object') {
            return findLocalIdForLibraryRef(annotation.s, refLocalId, libraryName);
        }
    }
    // if nothing above caused this to return, then we are at a leaf node and should return null
    return null;
}
exports.findLocalIdForLibraryRef = findLocalIdForLibraryRef;
/**
 * Figure out if a statement is a function given library and statement name.
 * @public
 * @param {ELM} libraryName - The name of the library the statement belongs to.
 * @param {string} statementName - The statement name to search for.
 * @return {boolean} If the statement is a function or not.
 */
function isStatementFunction(library, statementName) {
    // find the library and statement in the elm
    var statement = library.library.statements.def.find(function (def) { return def.name === statementName; });
    if (statement != null) {
        return statement.type === 'FunctionDef';
    }
    return false;
}
exports.isStatementFunction = isStatementFunction;
/**
 * Figure out if a statement is in a Supplemental Data Element given the statement name.
 * @public
 * @param {fhir4.MeasureSupplementalData[]} supplementalDataElements
 * @param {string} statementName - The statement to search for.
 * @return {boolean} Statement does or does not belong to a Supplemental Data Element.
 */
function isSupplementalDataElementStatement(supplementalDataElements, statementName) {
    if (supplementalDataElements != undefined) {
        for (var _i = 0, supplementalDataElements_1 = supplementalDataElements; _i < supplementalDataElements_1.length; _i++) {
            var supplementalData = supplementalDataElements_1[_i];
            if (supplementalData.criteria.language === 'text/cql' && supplementalData.criteria.expression === statementName) {
                return true;
            }
        }
    }
    return false;
}
exports.isSupplementalDataElementStatement = isSupplementalDataElementStatement;
function __guard__(value, transform) {
    return typeof value !== 'undefined' && value !== null ? transform(value) : undefined;
}
//# sourceMappingURL=ClauseResultsHelpers.js.map